import { useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';

export interface PickItem {
  value: string;
  label: string;
  /** Grey text after the label, shown only when two labels are the same. */
  detail?: string | undefined;
  /** Extra words the search matches, such as a code or an Urdu name. */
  keywords?: string | undefined;
}

/** Rows drawn at once. Past this the person types to narrow, so a list of
 *  thousands costs the page no more than a list of fifty. */
const ROW_LIMIT = 50;

/** Lists this short are plain checklists: no search, no bulk buttons. */
const PLAIN_UP_TO = 8;

function matcher(query: string) {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return (item: PickItem) => {
    if (terms.length === 0) return true;
    const text = `${item.label} ${item.detail ?? ''} ${item.keywords ?? ''}`.toLowerCase();
    return terms.every((t) => text.includes(t));
  };
}

/**
 * Choose several from a list of any length: type to find, tick to choose,
 * "Select all" for everything the search found, and "Chosen" to look over
 * the choice in the same box. The box never grows past a fixed height, and
 * never draws more than {@link ROW_LIMIT} rows.
 */
export function PickList({
  label,
  items,
  value,
  onChange,
  noun,
  searchPlaceholder,
  max,
  tooMany,
  unknownLabel = 'Not in your list',
  wide = false,
  blocked,
}: {
  /** What the list holds, for screen readers: "Roles". */
  label: string;
  items: PickItem[];
  value: string[];
  onChange: (next: string[]) => void;
  noun: { one: string; many: string };
  searchPlaceholder: string;
  /** The most the server accepts. */
  max?: number | undefined;
  /** Offered when "Select all" would pass `max`, such as "Choose Every farm instead". */
  tooMany?: { label: string; onClick: () => void } | undefined;
  /** For a chosen value no longer in `items`, such as a farm out of the person's scope. */
  unknownLabel?: string;
  /** One item per line, for long labels. */
  wide?: boolean;
  /**
   * Values that may be seen but not ticked — a role the server would refuse.
   * Listed rather than hidden, so the reason is read instead of guessed at;
   * put the reason in the item's own label.
   */
  blocked?: ReadonlySet<string> | undefined;
}) {
  const id = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [view, setView] = useState<'all' | 'chosen'>('all');
  const [overLimit, setOverLimit] = useState<number | null>(null);

  const chosen = useMemo(() => new Set(value), [value]);
  const plain = items.length <= PLAIN_UP_TO;

  // A label that appears twice gets its detail shown, so the two can be told apart.
  const repeated = useMemo(() => {
    const seen = new Map<string, number>();
    for (const i of items) seen.set(i.label, (seen.get(i.label) ?? 0) + 1);
    return new Set([...seen].filter(([, n]) => n > 1).map(([l]) => l));
  }, [items]);

  const matches = useMemo(() => {
    const match = matcher(query);
    if (view === 'all') return items.filter(match);
    const known = new Map(items.map((i) => [i.value, i]));
    return value.map((v) => known.get(v) ?? { value: v, label: unknownLabel }).filter(match);
  }, [items, value, view, query, unknownLabel]);

  const shown = matches.slice(0, ROW_LIMIT);
  const unchosenMatches = matches.filter((i) => !chosen.has(i.value) && !blocked?.has(i.value));
  const chosenMatches = matches.filter((i) => chosen.has(i.value));
  const full = max !== undefined && value.length >= max;

  const setQueryAndReset = (q: string) => {
    setQuery(q);
    setOverLimit(null);
  };

  const toggle = (v: string) => {
    setOverLimit(null);
    if (chosen.has(v)) onChange(value.filter((x) => x !== v));
    else if (!full && !blocked?.has(v)) onChange([...value, v]);
  };

  const selectAll = () => {
    const next = value.length + unchosenMatches.length;
    if (max !== undefined && next > max) {
      setOverLimit(next);
      return;
    }
    onChange([...value, ...unchosenMatches.map((i) => i.value)]);
  };

  const clearMatches = () => {
    const drop = new Set(matches.map((i) => i.value));
    onChange(value.filter((v) => !drop.has(v)));
  };

  const boxes = () => Array.from(listRef.current?.querySelectorAll<HTMLInputElement>('input[type="checkbox"]') ?? []);

  const onSearchKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // Type, Enter, type the next one: the top match is ticked and the box
      // empties for the next search. Never submits the surrounding form.
      e.preventDefault();
      const top = shown.find((i) => !chosen.has(i.value) && !blocked?.has(i.value));
      if (top && query.trim() && !full) {
        onChange([...value, top.value]);
        setQueryAndReset('');
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      boxes().find((b) => !b.disabled)?.focus();
    }
  };

  const onListKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const all = boxes().filter((b) => !b.disabled);
    const at = all.indexOf(document.activeElement as HTMLInputElement);
    if (at === -1) return;
    e.preventDefault();
    const next = at + (e.key === 'ArrowDown' ? 1 : -1);
    if (next < 0) document.getElementById(`${id}-search`)?.focus();
    else all[Math.min(next, all.length - 1)]?.focus();
  };

  const searching = query.trim() !== '';
  const total = items.length;

  return (
    <div className={`picklist${wide ? ' picklist--wide' : ''}`}>
      {!plain && (
        <div className="picklist__bar">
          <input
            id={`${id}-search`}
            className="field__input picklist__search"
            type="search"
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            value={query}
            onChange={(e) => setQueryAndReset(e.target.value)}
            onKeyDown={onSearchKey}
            autoComplete="off"
          />
          <div className="viewtoggle picklist__view" role="group" aria-label="Show">
            {(['all', 'chosen'] as const).map((v) => (
              <button
                key={v}
                type="button"
                aria-pressed={view === v}
                className={`viewtoggle__btn${view === v ? ' viewtoggle__btn--on' : ''}`}
                onClick={() => {
                  setView(v);
                  setOverLimit(null);
                }}
              >
                {v === 'all' ? 'All' : `Chosen (${value.length})`}
              </button>
            ))}
          </div>
        </div>
      )}

      {!plain && view === 'all' && matches.length > 0 && (
        <div className="picklist__bulk">
          {unchosenMatches.length > 0 ? (
            <button type="button" className="btn btn--sm" onClick={selectAll}>
              {searching
                ? `Select all ${unchosenMatches.length} matching`
                : `Select all ${unchosenMatches.length}`}
            </button>
          ) : (
            // Only when there is something to untick: a search whose every
            // match is blocked has none chosen, and offering to untick them
            // would be a button that does nothing.
            searching &&
            chosenMatches.length > 0 && (
              <button type="button" className="btn btn--sm btn--ghost" onClick={clearMatches}>
                Untick all {chosenMatches.length} matching
              </button>
            )
          )}
          {searching && unchosenMatches.length > 0 && chosenMatches.length > 0 && (
            <button type="button" className="btn btn--sm btn--ghost" onClick={clearMatches}>
              Untick the {chosenMatches.length} chosen
            </button>
          )}
        </div>
      )}

      {overLimit !== null && (
        <p className="picklist__limit" role="status">
          That would be {overLimit} {noun.many}, more than the {max} one announcement can name.{' '}
          {tooMany ? (
            <button type="button" className="linkbutton" onClick={tooMany.onClick}>
              {tooMany.label}
            </button>
          ) : (
            'Type to narrow the search.'
          )}
        </p>
      )}

      <div
        ref={listRef}
        className="picklist__list"
        role="group"
        aria-label={label}
        onKeyDown={onListKey}
      >
        {shown.map((item) => {
          const on = chosen.has(item.value);
          return (
            <label key={item.value} className={`settings__toggle picklist__row${on ? ' picklist__row--on' : ''}`}>
              <input
                type="checkbox"
                checked={on}
                disabled={!on && (full || Boolean(blocked?.has(item.value)))}
                onChange={() => toggle(item.value)}
              />
              <span className="picklist__text">
                <span className="picklist__label">{item.label}</span>
                {item.detail && repeated.has(item.label) && (
                  <>
                    {' '}
                    <span className="picklist__detail">{item.detail}</span>
                  </>
                )}
              </span>
            </label>
          );
        })}
        {shown.length === 0 && (
          <p className="muted picklist__empty">
            {view === 'chosen' && !searching
              ? `No ${noun.many} chosen yet.`
              : `No ${noun.one} matches “${query.trim()}”.`}
          </p>
        )}
      </div>

      <p className="settings__hint picklist__foot">
        {matches.length > ROW_LIMIT && (
          <>
            Showing {ROW_LIMIT} of {matches.length}. Type more to narrow.{' '}
          </>
        )}
        <span>
          {value.length} of {total} {noun.many} chosen
        </span>
        {full && <> · that is the most one announcement can name</>}
        {value.length > 0 && (
          <>
            {' · '}
            <button
              type="button"
              className="linkbutton"
              onClick={() => {
                onChange([]);
                setOverLimit(null);
              }}
            >
              Clear
            </button>
          </>
        )}
      </p>
    </div>
  );
}
